import { format } from 'date-fns';
import { isNotNullish } from '@adornis/assert/isNullish.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { stateful, useEffect, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import type { DateSelectArg, EventClickArg } from '@fullcalendar/core';
import { computed } from '@lit-labs/preact-signals';
import { html, nothing } from 'lit';
import { keyed } from 'lit/directives/keyed.js';
import { toCalendarDay } from '@adornis/datetime/calendarDay.js';
import { Observable } from 'rxjs';
import { showAbwesenheitBeantragenDialog } from '../../client/components/dialogs/sg-abwesenheit-beantragen-dialog.js';
import { showAbwesenheitDetailDialog } from '../../client/components/dialogs/sg-abwesenheit-detail-dialog.js';
import { AbwesenheitAPI } from '../../abwesenheiten/api/AbwesenheitAPI.js';
import { Abwesenheit } from '../../db/abwesenheit.js';
import { DialogResult } from '../../db/enums/misc-enums.js';
import { AbsenceApprovalStatus } from '../../db/enums/status.js';
import { getSchoolIDsForRoles } from '../../permissions/api/getSchoolIDsForRoles.js';
import { Role } from '../../permissions/roles.js';
import { InterviewAPI } from '../../sus-entry-form/interviews/api/InterviewAPI.js';
import { sgInterviewTeacherChooseSlot } from '../../sus-entry-form/interviews/client/sg-interview-teacher-choose-slot.js';
import type { InterviewSlot } from '../../sus-entry-form/interviews/entities/InterviewSlot.js';
import { Person } from '../../users/entities/person.js';
import { importUnitsAbwesenheiten } from './importUnitsAbwesenheiten.js';
import { mapEntitiesToCalenderEvents } from './mapEntitiesToCalenderEvents.js';
import './sg-calendar-component.js';
import { PageHeader } from '@martinus/styles/client/PageHeader.js';

async function handleAbwesenheitClicked(abwesenheit: Abwesenheit) {
  const readonly = abwesenheit.genehmigungsStatus !== AbsenceApprovalStatus.REQUESTED;
  const dialogResult = await showAbwesenheitDetailDialog({ abwesenheit, readonly });

  if (dialogResult === DialogResult.DELETE) await AbwesenheitAPI.remove(abwesenheit._id)();
}

export const SGCalender = stateful((props: { personID: Maybe<string> }) => {
  const user = useObservable(() => currentUser(), []);

  const { value: person, loading: personLoading } = useQuery(
    () => subscribeByID(Person, props.personID)({ _id: 1, roles: { name: 1, contexts: 1 } }),
    [props.personID],
  );

  const { value: abwesenheiten, loading: abwesenheitenLoading } = useQuery(
    () =>
      AbwesenheitAPI.subscribeForPerson(props.personID ?? '')({
        _id: 1,
        start: 1,
        ende: 1,
        ganztaegig: 1,
        partOfTheDay: 1,
        genehmigungsStatus: 1,
        kommentar: 1,
        _class: 1,
      }),
    [props.personID],
  );

  const [schoolIDs, setSchoolIDs] = useState<string[]>([]);

  useEffect(() => {
    if (user) {
      void getSchoolIDsForRoles(user, [Role.SUS_BEWERBER_BEARBEITUNG, Role.MITARBEITER]).then(setSchoolIDs);
    }
  }, [user]);

  const { value: susInterviewSlots, loading: susInterviewSlotsLoading } = useQuery(
    () =>
      InterviewAPI.subscribeAll(schoolIDs)({
        _id: 1,
        startDate: 1,
        endDate: 1,
        interviewerID: 1,
        schoolID: 1,
        childID: 1,
      }),
    [props.personID, schoolIDs],
  );

  const events = computed(() => {
    const eventsGroupedByTimeSlot = Object.values(
      Object.groupBy(susInterviewSlots ?? [], slot =>
        slot.startDate ? format(slot.startDate, 'yyyy-MM-dd HH:mm') : '',
      ),
    ).filter(isNotNullish);

    const entityEvents: Array<Abwesenheit | InterviewSlot[]> = [...(abwesenheiten ?? []), ...eventsGroupedByTimeSlot];

    return new Observable(subscriber => subscriber.next(mapEntitiesToCalenderEvents(entityEvents)));
  });

  if (personLoading || abwesenheitenLoading || susInterviewSlotsLoading || !user || !person)
    return html`<div class="flex flex-col h-full w-full gap-4 m-4 items-center justify-center">
      <span class="loading loading-spinner"></span>
    </div>`;

  return html`
    <div class="flex flex-col gap-4 m-4">
      <div class="card flex flex-row p-4 gap-4 items-center">
        <button
          class="btn btn-primary btn-lg"
          @click=${() =>
            void showAbwesenheitBeantragenDialog({
              personID: person._id,
              ganztaegig: true,
            })}
        >
          <span class="icon-[material-symbols--add-rounded] size-5"></span>
          Termin eintragen
        </button>
        ${
          user.roles.some(r => r.name === Role.ADMIN) && person.roles.some(r => r.name === Role.ADMIN)
            ? html` <button class="btn btn-ghost" @click=${importUnitsAbwesenheiten}>Abwesenheit Import</button> `
            : nothing
        }
      </div>
      <div class="card flex flex-row p-4 gap-4">
        ${keyed(
          events.value,
          html`
            <sg-calendar-component
              selectable
              week
              month
              .eventsProvider=${() => events.value}
              .options=${{
                displayEventTime: false,
              }}
              @select=${(e: CustomEvent<DateSelectArg>) => {
                const ganztaegig = e.detail.view.type === 'dayGridMonth';
                // Absences are day precision. Fullcalendar hands out an exclusive end, so the last
                // included day is one millisecond before it; both ends are stored canonically.
                void showAbwesenheitBeantragenDialog({
                  personID: person._id,
                  start: toCalendarDay(e.detail.start),
                  ende: toCalendarDay(new Date(e.detail.end.getTime() - 1)),
                  ganztaegig,
                });
              }}
              @eventClick=${async (e: CustomEvent<EventClickArg>) => {
                const embeddedEntity: Abwesenheit | InterviewSlot[] = e.detail.event.extendedProps.entity;

                if (embeddedEntity instanceof Abwesenheit) {
                  await handleAbwesenheitClicked(embeddedEntity);
                } else {
                  await sgInterviewTeacherChooseSlot(embeddedEntity);
                }
              }}
            >
            </sg-calendar-component>
          `,
        )}
      </div>
    </div>
  `;
});

export const SGCalenderPage = stateful(
  (props: { personID: Maybe<string> }) => html`
    ${PageHeader({ title: 'Kalender' })}
    <div class="flex flex-col">${SGCalender({ personID: props.personID })}</div>
  `,
);
